import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Avatar,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close, CameraAlt, Upload, Delete } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import './AvatarUploadModal.scss';

interface AvatarUploadModalProps {
  open: boolean;
  onClose: () => void;
  currentAvatar: string;
  onUpload: (file: File) => Promise<void>;
  isUploading?: boolean;
}

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  open,
  onClose,
  currentAvatar,
  onUpload,
  isUploading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await onUpload(selectedFile);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
    onClose();
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveSelection = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          className="avatar-upload-modal"
          PaperProps={{
            component: motion.div,
            initial: { opacity: 0, scale: 0.9, y: 20 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.9, y: 20 },
            transition: { type: 'spring', stiffness: 260, damping: 20 },
          }}
        >
          <DialogTitle className="dialog-title">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight={600}>
                Update Profile Photo
              </Typography>
              <IconButton
                onClick={handleClose}
                size="small"
                className="close-button"
                disabled={isUploading}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent className="dialog-content">
            <Box className="upload-container">
              {/* Current/Preview Avatar */}
              <Box className="avatar-section">
                <Box className="avatar-container">
                  <Avatar
                    src={previewUrl || currentAvatar || undefined}
                    className="avatar-preview"
                    sx={{ width: 120, height: 120 }}
                  >
                    {!previewUrl && !currentAvatar && <CameraAlt sx={{ fontSize: 40 }} />}
                  </Avatar>

                  {/* Loading overlay */}
                  {isUploading && (
                    <Box className="loading-overlay">
                      <CircularProgress size={40} color="primary" />
                    </Box>
                  )}

                  {/* Camera overlay for upload */}
                  <Box
                    className="camera-overlay"
                    onClick={handleCameraClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleCameraClick();
                      }
                    }}
                  >
                    <CameraAlt sx={{ fontSize: 20 }} />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                  {selectedFile ? 'Preview' : 'Current Photo'}
                </Typography>
              </Box>

              {/* File Input */}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={isUploading}
              />

              {/* Upload Actions */}
              <Box className="upload-actions">
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={handleCameraClick}
                  fullWidth
                  className="upload-button"
                  disabled={isUploading}
                >
                  Choose Photo
                </Button>

                {selectedFile && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="selected-file-info"
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={handleRemoveSelection}
                        disabled={isUploading}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </motion.div>
                )}
              </Box>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                </motion.div>
              )}

              {/* Action Buttons */}
              <Box className="action-buttons">
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isUploading}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  fullWidth
                  className="upload-confirm-button"
                  startIcon={isUploading ? <CircularProgress size={16} /> : <CameraAlt />}
                >
                  {isUploading ? 'Uploading...' : 'Update Photo'}
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default AvatarUploadModal;